import { useTrackingSlice } from '@frontend/redux';
import { TRACKING_PARAMS } from '@frontend/redux/slices/trackingSlice';
import { Outlet } from '@frontend/web/components';
import { createRootRouteWithContext } from '@frontend/web/utils';
import { useLocation, useScrollRestoration } from '@tanstack/react-router';
import { useEffect } from 'react';
import type { AuthContext } from '../hooks';
import { useAnalytics } from '../hooks';

export type RedirectContext = {
  isClientLoading: boolean;
  isClientStatusLoading: boolean;
  isGrowthbookReady: boolean;
  isSignupChangesEnabled: boolean;
  membershipComplete: boolean;
  onboardingComplete: boolean;
  profileComplete: boolean;
};

export type RouterContext = {
  authentication: MaybeUndefined<AuthContext>;
  redirectContext: MaybeUndefined<RedirectContext>;
};

const Root = () => {
  useScrollRestoration();

  const location = useLocation();
  const { trackRouteChange } = useAnalytics();
  const { setImRef, setIrClickId } = useTrackingSlice();

  useEffect(() => {
    trackRouteChange({
      properties: {
        path: location.pathname,
        search: location.search as Record<string, string>,
        searchStr: location.searchStr,
        title: window.document.title,
        url: `${window.location.origin}${location.href}`,
      },
    });

    // Impact tracking parameters
    const searchParams = new URLSearchParams(window.location.search);
    const irClickId = searchParams.get(TRACKING_PARAMS.IRCLICKID);
    const imRef = searchParams.get(TRACKING_PARAMS.IM_REF);

    if (irClickId) {
      setIrClickId(irClickId);
    }
    if (imRef) {
      setImRef(imRef);
    }
  }, [location]);

  return <Outlet />;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
});
